import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { ApiError } from './types';

class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string = '') {
    // Get environment variables safely
    if (typeof window === 'undefined') {
      // Server-side (Next.js)
      baseURL = process.env.NEXT_PUBLIC_API_URL || baseURL;
    } else {
      // Client-side (Vite - process.env.VITE_API_URL is defined via vite.config.ts)
      baseURL = process.env.VITE_API_URL || baseURL;
    }
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor for auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token =
          typeof window !== 'undefined'
            ? localStorage.getItem('accessToken')
            : null;

        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: unknown) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const apiError: ApiError = {
          message: error.message || 'An error occurred',
          status: error.response?.status || 500,
          code: error.code,
        };

        // Note: 401 handling is done by api.ts which has token refresh logic
        // Only redirect if explicitly logged out or tokens are manually cleared
        // Don't clear tokens here to avoid race conditions with token refresh

        return Promise.reject(apiError);
      }
    );
  }

  // Generic request method
  private async request<T>(
    method: string,
    url: string,
    data?: unknown
  ): Promise<T> {
    const response = await this.client.request({
      method,
      url,
      data,
    });
    return response.data;
  }

  // HTTP methods
  async get<T>(url: string): Promise<T> {
    return this.request<T>('GET', url);
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>('POST', url, data);
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>('PUT', url, data);
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>('PATCH', url, data);
  }

  async delete<T>(url: string): Promise<T> {
    return this.request<T>('DELETE', url);
  }

  // Set auth token
  setAuthToken(token: string) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('accessToken', token);
    }
  }

  // Remove auth token
  clearAuthToken() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }
}

export const apiClient = new ApiClient();
